import { useEffect, useRef } from 'react';

import { logError } from '@zg-rentals/log-error';
import { useMutation, useQueryClient } from '@zg-rentals/react-query5';
import { RentalManagerUserPropertySpotlightV2WebApiHandler_optimizationStatus } from '@zg-rentals/ts-rental-manager-api-client';

import {
  refreshSpotlightAdvertisingPastCache,
  refreshSpotlightOverviewPastCache,
  refreshSpotlightTable,
} from '../utils/cacheHelpers';

const STATUS_POLL_INTERVAL_MS = 1000;
const STATUS_POLL_MAX_ATTEMPTS = 30;

// overview and each row's advertising are served from a short-lived per-process cache on the API side, and a write
// only evicts the instance that handled it. The refresh that follows completion can therefore still be answered from a
// pre-engine snapshot, which leaves the row drawing no Spotlight chip and nothing to correct it. One refresh past the
// cache window repairs it. This tracks a backend constant, so it is a stopgap for fixing the stale read server side.
const PLACEMENT_CACHE_TTL_MS = 5000;
const RECONCILE_DELAY_MS = PLACEMENT_CACHE_TTL_MS + 1000;

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

// Resolves once the engine reports completion, or after the attempt cap so a stuck run can't hold the table locked
// forever. A failed status read ends the wait right away: the table refresh still runs, and an unavailable status
// endpoint degrades to an immediate refresh instead of locking the table for the whole cap.
const waitForOptimization = async () => {
  for (let attempt = 0; attempt < STATUS_POLL_MAX_ATTEMPTS; attempt += 1) {
    const status = await RentalManagerUserPropertySpotlightV2WebApiHandler_optimizationStatus().catch((error) => {
      logError({ error });
      return null;
    });
    if (!status || status.completed) {
      return;
    }
    await sleep(STATUS_POLL_INTERVAL_MS);
  }
};

/**
 * Returns a callback that waits for the engine work a spotlight change kicked off, then invalidates the table once and
 * re-pulls statuses for every listing. The status endpoint is keyed on the signed-in user's company rather than on a
 * per-request id, so it takes nothing from the mutation response and the wait survives a page reload. The wait runs as
 * a 'spotlight' mutation so the table stays locked until it lands.
 *
 * The reconciling refresh is scheduled outside the mutation on purpose: awaiting it would hold the table lock for the
 * whole cache window, which also swallows clicks on the rows the user is waiting for.
 */
export const useAwaitSpotlightOptimization = () => {
  const queryClient = useQueryClient();
  const reconcileTimeout = useRef<number | undefined>(undefined);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      window.clearTimeout(reconcileTimeout.current);
    };
  }, []);

  const { mutateAsync } = useMutation({
    mutationFn: async () => {
      await waitForOptimization();
      await refreshSpotlightTable(queryClient);
      // Last on purpose: the invalidate above refetches both with the original params, so doing these first would
      // let those refetches overwrite the authoritative values with the snapshot they were meant to replace.
      await Promise.all([
        refreshSpotlightOverviewPastCache(queryClient),
        refreshSpotlightAdvertisingPastCache(queryClient),
      ]);
    },
    meta: { context: { api: 'spotlight', details: 'await spotlight optimization' } },
  });

  return () => {
    // Cancelled as the new wait starts, not when it ends: a reconcile still pending from the previous change would
    // otherwise land mid-flight and overwrite the placement this one just wrote.
    window.clearTimeout(reconcileTimeout.current);
    void mutateAsync().finally(() => {
      // The wait outlives the panel, so its tail can run after cleanup. Arming the timeout here would leave a refresh
      // nothing can cancel, firing against a page the user has already left.
      if (!isMounted.current) {
        return;
      }
      reconcileTimeout.current = window.setTimeout(() => {
        void refreshSpotlightTable(queryClient);
      }, RECONCILE_DELAY_MS);
    });
  };
};
